/**
 * Feature Access Hook
 * Provides access to subscription plan limits and feature availability
 */

import { useEffect, useState } from 'react';
import {
  useGetPlanLimitsQuery,
  useGetUsageStatsQuery,
  useGetApproachingLimitsQuery,
} from '../store/api/apiSlice';

export interface PlanLimits {
  maxUsers: number;
  maxProjects: number;
  maxTeams: number;
  aiActionsPerMonth: number;
  storageQuotaBytes: number;
  maxAttachmentFileBytes?: number;
  aiEnabled: boolean;
  features: {
    advancedReporting: boolean;
    integrations: boolean;
    sso: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    webhooks: boolean;
    customRoles: boolean;
    whiteLabel: boolean;
    onPremise: boolean;
    aiSprintPlanning: boolean;
    aiWeeklyInsights: boolean;
    aiRiskDetection: boolean;
    aiResourceForecasting: boolean;
    prioritySupport: boolean;
    dedicatedManager: boolean;
  };
  supportLevel: string;
  supportResponseTimeHours: number;
}

export interface UsageMetric {
  current: number;
  limit: number | string;
  percentage: number;
}

export interface Usage {
  users: UsageMetric;
  projects: UsageMetric;
  teams: UsageMetric;
  aiActions: UsageMetric & { resetDate: Date };
  storage: {
    currentBytes: number;
    limitBytes: number;
    percentage: number;
  };
}

export interface PlanInfo {
  plan: 'SOLO' | 'TEAM' | 'ENTERPRISE';
  tenantName: string;
  limits: PlanLimits;
  upgradePath: 'SOLO' | 'TEAM' | 'ENTERPRISE' | null;
}

export interface UsageStats {
  plan: 'SOLO' | 'TEAM' | 'ENTERPRISE';
  limits: PlanLimits;
  usage: Usage;
}

export interface ApproachingLimitsWarning {
  type: string;
  message: string;
  percentage: number;
  current: number;
  limit: number | string;
}

export interface ApproachingLimits {
  hasWarnings: boolean;
  warnings: ApproachingLimitsWarning[];
  plan: 'SOLO' | 'TEAM' | 'ENTERPRISE';
  upgradePath: 'SOLO' | 'TEAM' | 'ENTERPRISE' | null;
}

/**
 * Hook to access feature availability and plan limits
 */
export function useFeatureAccess() {
  const { data: planInfo, isLoading: isLoadingPlan } = useGetPlanLimitsQuery(undefined);
  const { data: usageStats, isLoading: isLoadingUsage } = useGetUsageStatsQuery(undefined);
  const { data: approachingLimits, isLoading: isLoadingWarnings } = useGetApproachingLimitsQuery(undefined);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(isLoadingPlan || isLoadingUsage || isLoadingWarnings);
  }, [isLoadingPlan, isLoadingUsage, isLoadingWarnings]);

  const isUnlimitedLimit = (limit: number | string | undefined): boolean =>
    limit === 'unlimited' || limit === -1;

  /**
   * Check if a specific feature is available
   */
  const hasFeature = (feature: keyof PlanLimits['features']): boolean => {
    if (!planInfo?.limits) return false;
    return planInfo.limits.features[feature] || false;
  };

  /**
   * Check if a resource limit has been reached
   */
  const isLimitReached = (type: 'users' | 'projects' | 'teams' | 'aiActions' | 'storage'): boolean => {
    if (!usageStats?.usage) return false;
    const metric = usageStats.usage[type];

    if (isUnlimitedLimit(metric.limit)) return false;

    // For storage, check bytes
    if (type === 'storage') {
      const storage = usageStats.usage.storage;
      return storage.currentBytes >= storage.limitBytes;
    }

    return metric.current >= Number(metric.limit);
  };

  /**
   * Check if approaching a limit (>80% usage)
   */
  const isApproachingLimit = (type: 'users' | 'projects' | 'teams' | 'aiActions' | 'storage'): boolean => {
    if (!usageStats?.usage) return false;
    const metric = usageStats.usage[type];

    if (isUnlimitedLimit(metric.limit)) return false;

    return metric.percentage > 80;
  };

  /**
   * Get formatted display string for a limit
   */
  const getLimitDisplay = (type: 'users' | 'projects' | 'teams' | 'aiActions' | 'storage'): string => {
    if (!usageStats?.usage) return 'Loading...';
    const metric = usageStats.usage[type];

    if (isUnlimitedLimit(metric.limit)) {
      return `${metric.current} / Unlimited`;
    }

    if (type === 'storage') {
      const storage = usageStats.usage.storage;
      const usedMB = (storage.currentBytes / (1024 * 1024)).toFixed(1);
      const totalMB = (storage.limitBytes / (1024 * 1024)).toFixed(0);
      return `${usedMB}MB / ${totalMB}MB`;
    }

    return `${metric.current} / ${metric.limit}`;
  };

  /**
   * Get usage percentage (0-100)
   */
  const getUsagePercentage = (type: 'users' | 'projects' | 'teams' | 'aiActions' | 'storage'): number => {
    if (!usageStats?.usage) return 0;
    const metric = usageStats.usage[type];
    return metric.percentage;
  };

  /**
   * Check if AI features are available
   */
  const canUseAI = (): boolean => {
    if (!planInfo?.limits) return false;
    return planInfo.limits.aiEnabled;
  };

  /**
   * Get the current subscription plan
   */
  const getCurrentPlan = (): 'SOLO' | 'TEAM' | 'ENTERPRISE' | null => {
    const tier = planInfo?.usageTier;
    if (tier === 'starter') return 'SOLO';
    if (tier === 'growth') return 'TEAM';
    if (tier === 'enterprise') return 'ENTERPRISE';
    return null;
  };

  /**
   * Get the recommended upgrade path
   */
  const getUpgradePath = (): 'SOLO' | 'TEAM' | 'ENTERPRISE' | null => {
    const path = planInfo?.upgradePath;
    if (path === 'starter') return 'SOLO';
    if (path === 'growth') return 'TEAM';
    if (path === 'enterprise') return 'ENTERPRISE';
    return null;
  };

  /**
   * Check if there are any warnings about approaching limits
   */
  const hasApproachingLimitWarnings = (): boolean => {
    return approachingLimits?.hasWarnings || false;
  };

  /**
   * Get all approaching limit warnings
   */
  const getApproachingLimitWarnings = (): ApproachingLimitsWarning[] => {
    return approachingLimits?.warnings || [];
  };

  return {
    // Loading state
    isLoading,
    
    // Raw data
    planInfo: planInfo || null,
    usageStats: usageStats || null,
    approachingLimits: approachingLimits || null,
    
    // Helper functions
    hasFeature,
    isLimitReached,
    isApproachingLimit,
    getLimitDisplay,
    getUsagePercentage,
    canUseAI,
    getCurrentPlan,
    getUpgradePath,
    hasApproachingLimitWarnings,
    getApproachingLimitWarnings,
  };
}

/**
 * Hook to check if currently at a limit (for immediate blocking)
 */
export function useResourceLimit(type: 'users' | 'projects' | 'teams' | 'aiActions' | 'storage') {
  const { isLimitReached, getLimitDisplay, getUsagePercentage, isLoading } = useFeatureAccess();
  
  return {
    isAtLimit: isLimitReached(type),
    display: getLimitDisplay(type),
    percentage: getUsagePercentage(type),
    isLoading,
  };
}

/**
 * Hook to check feature availability
 */
export function useFeature(feature: keyof PlanLimits['features']) {
  const { hasFeature, getCurrentPlan, getUpgradePath, isLoading } = useFeatureAccess();
  
  return {
    isAvailable: hasFeature(feature),
    currentPlan: getCurrentPlan(),
    upgradePath: getUpgradePath(),
    isLoading,
  };
}
